/**
 * Surprav Storefront Telemetry Layer
 *
 * Tracks product impressions, clicks, add-to-cart actions and checkout events.
 * Events are batched (every 5s or at 10 events) and sent via navigator.sendBeacon
 * with fetch as fallback. Supports offline queuing and retry on failure.
 *
 * @see design.md — Telemetry Layer section
 */

// ── Schema constants ─────────────────────────────────────────────────────────

const SCHEMA_VERSION = 1;
const BATCH_INTERVAL_MS = 5000;
const BATCH_SIZE_THRESHOLD = 10;
const MAX_RETRIES = 3;
const OFFLINE_STORAGE_KEY = 'surprav_telemetry_offline';
const TELEMETRY_ENDPOINT = '/api/events';

// ── Valid event types and sources ────────────────────────────────────────────

const VALID_EVENT_TYPES = ['impression', 'click', 'add_to_cart', 'checkout_started'];
const VALID_SOURCES = ['grid', 'discovery', 'search', 'product-page'];

// ── UUID v4 generator ────────────────────────────────────────────────────────

function generateSessionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0;
    var v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── Schema validation ────────────────────────────────────────────────────────

function validateEvent(event) {
  if (!event || typeof event !== 'object') return false;
  if (!event.productId || typeof event.productId !== 'string') return false;
  if (!event.variantId || typeof event.variantId !== 'string') return false;
  if (!event.timestamp || typeof event.timestamp !== 'number') return false;
  if (!event.source || typeof event.source !== 'string') return false;
  if (!event.sessionId || typeof event.sessionId !== 'string') return false;
  if (event.schemaVersion == null || typeof event.schemaVersion !== 'number' || event.schemaVersion < 1) return false;
  if (!VALID_EVENT_TYPES.includes(event.type)) return false;
  return true;
}

// ── StorefrontTelemetry class ────────────────────────────────────────────────

class StorefrontTelemetry {
  constructor(options) {
    options = options || {};
    this._queue = [];
    this._sessionId = options.sessionId || generateSessionId();
    this._endpoint = options.endpoint || TELEMETRY_ENDPOINT;
    this._batchInterval = options.batchInterval || BATCH_INTERVAL_MS;
    this._batchSize = options.batchSize || BATCH_SIZE_THRESHOLD;
    this._timer = null;
    this._observers = new Map();
    this._retryCount = {};

    this._startBatchTimer();
    this._setupVisibilityHandler();
    this._setupOnlineHandler();
  }

  // ── Public tracking methods ──────────────────────────────────────────────

  /**
   * Track a product impression via IntersectionObserver.
   * @param {string} productId
   * @param {string} variantId
   * @param {string} source — one of: grid, discovery, search, product-page
   */
  trackImpression(productId, variantId, source) {
    this._addEvent('impression', productId, variantId, source);
  }

  /**
   * Track a product click.
   * @param {string} productId
   * @param {string} variantId
   * @param {string} source
   */
  trackClick(productId, variantId, source) {
    this._addEvent('click', productId, variantId, source);
  }

  /**
   * Track an add-to-cart action.
   * @param {string} productId
   * @param {string} variantId
   * @param {string} source
   */
  trackAddToCart(productId, variantId, source) {
    this._addEvent('add_to_cart', productId, variantId, source);
  }

  /**
   * Track checkout started.
   */
  trackCheckoutStarted() {
    this._addEvent('checkout_started', '', '', '');
  }

  /**
   * Observe a DOM element for impression tracking via IntersectionObserver.
   * @param {Element} element — DOM element with data-product-id
   * @param {string} productId
   * @param {string} variantId
   * @param {string} source
   */
  observeImpression(element, productId, variantId, source) {
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: track immediately if IntersectionObserver not available
      this.trackImpression(productId, variantId, source);
      return;
    }

    var self = this;
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          self.trackImpression(productId, variantId, source);
          observer.unobserve(entries[i].target);
          self._observers.delete(element);
        }
      }
    }, { threshold: 0.5 });

    observer.observe(element);
    this._observers.set(element, observer);
  }

  // ── Internal event creation ──────────────────────────────────────────────

  /**
   * Create and queue a telemetry event.
   * @private
   */
  _addEvent(type, productId, variantId, source) {
    var event = {
      type: type,
      productId: String(productId || ''),
      variantId: String(variantId || ''),
      timestamp: Date.now(),
      source: String(source || ''),
      sessionId: this._sessionId,
      schemaVersion: SCHEMA_VERSION,
    };

    this._queue.push(event);

    // Flush immediately if batch size threshold reached
    if (this._queue.length >= this._batchSize) {
      this._flush();
    }
  }

  // ── Batch timer ──────────────────────────────────────────────────────────

  /**
   * Start the periodic batch flush timer.
   * @private
   */
  _startBatchTimer() {
    if (this._timer) return;
    var self = this;
    this._timer = setInterval(function () {
      self._flush();
    }, this._batchInterval);
  }

  /**
   * Stop the batch timer.
   * @private
   */
  _stopBatchTimer() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  // ── Flush and send ───────────────────────────────────────────────────────

  /**
   * Flush all queued events — send via sendBeacon or fetch.
   * Also retries any previously failed events from localStorage.
   * @private
   */
  _flush() {
    // Load any offline/retry events first
    var retryEvents = this._loadOfflineEvents();
    var allEvents = retryEvents.concat(this._queue);
    this._queue = [];

    if (allEvents.length === 0) return;

    // Clear offline storage since we're attempting to send
    this._clearOfflineEvents();

    var sent = this._send(allEvents);
    if (!sent) {
      this._saveEventsForRetry(allEvents);
    }
  }

  /**
   * Send events via sendBeacon with fetch as fallback.
   * @param {Array} events
   * @returns {boolean} true if send was initiated successfully
   * @private
   */
  _send(events) {
    if (events.length === 0) return true;

    // Check if offline
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }

    var payload = JSON.stringify({ events: events, sentAt: Date.now() });

    // Try sendBeacon first
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        var blob = typeof Blob !== 'undefined'
          ? new Blob([payload], { type: 'application/json' })
          : payload;
        var success = navigator.sendBeacon(this._endpoint, blob);
        if (success) return true;
      } catch (_e) {
        // Fall through to fetch
      }
    }

    // Fallback to fetch
    if (typeof fetch === 'function') {
      fetch(this._endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(function () {
        // Silent failure — telemetry must never impact UX
      });
      return true;
    }

    return false;
  }

  // ── Visibility change handler ────────────────────────────────────────────

  /**
   * Set up visibilitychange as last-chance flush.
   * @private
   */
  _setupVisibilityHandler() {
    if (typeof document === 'undefined') return;
    var self = this;
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        self._flush();
      }
    });
  }

  // ── Online/offline handlers ──────────────────────────────────────────────

  /**
   * Set up online event handler to flush stored events on reconnect.
   * @private
   */
  _setupOnlineHandler() {
    if (typeof window === 'undefined') return;
    var self = this;
    window.addEventListener('online', function () {
      self._flush();
    });
  }

  // ── Offline storage (localStorage) ───────────────────────────────────────

  /**
   * Save events to localStorage for retry.
   * Increments retry count per batch. Drops events exceeding MAX_RETRIES.
   * @param {Array} events
   * @private
   */
  _saveEventsForRetry(events) {
    if (typeof localStorage === 'undefined') return;

    // Tag events with retry count
    var eventsToSave = [];
    for (var i = 0; i < events.length; i++) {
      var evt = events[i];
      var key = evt.timestamp + ':' + evt.type + ':' + evt.productId + ':' + i;
      if (!evt._retryKey) {
        evt._retryKey = key;
      }
      var retries = this._retryCount[evt._retryKey] || 0;
      if (retries < MAX_RETRIES) {
        this._retryCount[evt._retryKey] = retries + 1;
        eventsToSave.push(evt);
      }
      // Events exceeding MAX_RETRIES are silently dropped
    }

    if (eventsToSave.length === 0) return;

    try {
      var existing = this._loadOfflineEvents();
      var combined = existing.concat(eventsToSave);
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(combined));
    } catch (_e) {
      // localStorage full — remove oldest events (FIFO)
      this._pruneOfflineStorage(eventsToSave);
    }
  }

  /**
   * Load events from localStorage.
   * @returns {Array}
   * @private
   */
  _loadOfflineEvents() {
    if (typeof localStorage === 'undefined') return [];
    try {
      var raw = localStorage.getItem(OFFLINE_STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  /**
   * Clear offline events from localStorage.
   * @private
   */
  _clearOfflineEvents() {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
    } catch (_e) {
      // Ignore
    }
  }

  /**
   * Prune oldest events from localStorage to make room for new ones (FIFO).
   * @param {Array} newEvents
   * @private
   */
  _pruneOfflineStorage(newEvents) {
    if (typeof localStorage === 'undefined') return;
    try {
      var existing = this._loadOfflineEvents();
      // Remove oldest half to make room
      var half = Math.ceil(existing.length / 2);
      var pruned = existing.slice(half);
      var combined = pruned.concat(newEvents);
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(combined));
    } catch (_e) {
      // If still failing, try saving just the new events
      try {
        localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(newEvents));
      } catch (_e2) {
        // Give up — graceful degradation
      }
    }
  }

  /**
   * Destroy the telemetry instance — stop timers and disconnect observers.
   */
  destroy() {
    this._stopBatchTimer();
    this._flush();
    this._observers.forEach(function (observer) {
      observer.disconnect();
    });
    this._observers.clear();
  }
}

// ── Exports ──────────────────────────────────────────────────────────────────

// Export for Node.js testing
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { StorefrontTelemetry, validateEvent, SCHEMA_VERSION, VALID_EVENT_TYPES, VALID_SOURCES };
}

// Browser: attach to window.Surprav namespace
if (typeof window !== 'undefined') {
  window.Surprav = window.Surprav || {};
  window.Surprav.Telemetry = new StorefrontTelemetry();
}
